/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionClear';

const ActionClearManager: ActionManagerFn<'ActionClear'> = (
  platform: Platform,
): ActionManager<'ActionClear'> => {
  return {
    ...baseActionManager(platform, 'ActionClear'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
      force: {
        label: 'Force',
        type: 'boolean',
      },
    },
    name: 'Clear Data Store',
    getDisplayText: (item) => item.props.alias,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'eraser' },
    replaceableWith: [
      'ActionRefresh',
      'ActionReset',
      'ActionSave',
      'ActionClearStoreCache',
    ],
    description: `**Overview**<br/>
The Clear Data Store action is a data management component designed to purge all records from a specific Data Store. It serves as a reset mechanism, effectively emptying the contents of the selected data entity within the application's runtime or persistent state.<br/><br/>
This component is highly integrated into the data store ecosystem, featuring broad compatibility with various store types (Cloud, Local, Transient, etc.) and implementing logic to track where specific data source aliases are utilized within the application's logic.

**Key Features**<br/>
* **Broad Data Support:** Configured to accept a wide range of data store types via the alias property, including CloudDataStore, ReadOnlyDataStore, DataStore, LocalDataStore, and TransientDataStore.
* **Component Interchangeability:** Explicitly defined in the replaceableWith array to be easily swapped with related data actions like ActionRefresh, ActionReset, ActionSave, and ActionClearStoreCache, allowing for rapid prototyping and logic adjustment.
* **Dependency Tracking:** Implements isDataSourceAliasUsed to programmatically report if a specific alias is currently being referenced by this action, aiding in dependency management and analysis.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property that identifies the target Data Store to be cleared.
* **Force:** A boolean property typically used to override validation or confirmation checks during the clear operation.

**Use Cases**<br/>
* **Session Cleanup:** Emptying a TransientDataStore or LocalDataStore when a user completes a workflow or logs out.
* **State Reset:** Removing all loaded records from a view-bound DataStore to prepare for a fresh search or data entry task.`,
  };
};

export default ActionClearManager;
